import { Component, inject, model, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthenticationService } from 'authentication/services/authentication.service';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import { catchError, switchMap, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TabOutlineComponent } from 'shared/components/tab-outline/tab-outline.component';
import { Tab, TabKeyEnum } from 'shared/interfaces/tab.interface';

@Component({
    selector: 'app-dashboard-wrapper',
    imports: [RouterModule, TabOutlineComponent],
    templateUrl: './dashboard-wrapper.component.html',
    styleUrl: './dashboard-wrapper.component.scss',
})
export class DashboardWrapperComponent implements OnInit {
    private router = inject(Router);
    private visualsDashboardService = inject(VisualsDashboardService);
    private authenticationService = inject(AuthenticationService);

    hasVisualsDashboardAccess = signal<boolean | null>(null);

    private readonly allTabs: Tab[] = [
        {
            label: 'visuals.visualsContentSplit.contentArea.title',
            link: '/dashboard/content',
            key: TabKeyEnum.CONTENT_AREA,
            icon: 'fa fa-folder-grid',
        },
        {
            label: 'visuals.visualsContentSplit.visualsArea.title',
            link: '/dashboard/visuals',
            key: TabKeyEnum.VISUALS_AREA,
            icon: 'fa fa-image',
        },
    ];

    tabs = signal<Tab[]>([]);
    selectedTabIndex = model(0);
    tabKeyEnum = TabKeyEnum;

    constructor() {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.syncSelectedTabIndex();
        });
    }

    ngOnInit(): void {
        this.checkVisualsDashboardAccess();
    }

    private checkVisualsDashboardAccess(): void {
        this.visualsDashboardService
            .obtainDashboardTokenAsObservable()
            .pipe(
                take(1),
                catchError(() =>
                    this.authenticationService.renewIdentityTokenAsObservable().pipe(
                        switchMap(() => this.visualsDashboardService.obtainDashboardTokenAsObservable()),
                        take(1),
                    ),
                ),
            )
            .subscribe({
                next: () => {
                    this.hasVisualsDashboardAccess.set(true);
                    this.tabs.set(this.allTabs);
                    this.syncSelectedTabIndex();
                },
                error: () => {
                    this.hasVisualsDashboardAccess.set(false);
                    this.tabs.set([]);
                    this.redirectToContentIfNeeded();
                },
            });
    }

    private syncSelectedTabIndex(): void {
        const currentUrl = this.router.url;
        const currentTabs = this.tabs();
        const foundIndex = currentTabs.findIndex((tab) => currentUrl.startsWith(tab.link || ''));
        if (foundIndex >= 0) {
            this.selectedTabIndex.set(foundIndex);
        }
    }

    private redirectToContentIfNeeded(): void {
        if (this.router.url.startsWith('/dashboard/visuals')) {
            this.router.navigate(['/dashboard/content']);
        }
    }
}
